import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { CountUp } from './CountUp';
import { StakeVaultPanel } from './StakeVaultPanel';
import { StakePositionCard } from './StakePositionCard';
import { StakeWithdrawSheet } from './StakeWithdrawSheet';
import { StakeLockSheet } from './StakeLockSheet';
import { StakeShareCard } from './StakeShareCard';
import { StakeTapSafe } from './StakeTapSafe';
import { Confetti } from './Confetti';
import { EyeIcon, EyeOffIcon } from './icons';
import { fmtUsd, positionMeta, type PositionMeta } from './stakeMeta';
import { useStaking, useStakingActions } from '../../hooks/useStaking';
import { stakingErrorMessage, type StakePosition, type VaultKind } from '../../lib/staking';
import { HIDDEN_BALANCE, STAKING_BALANCE_ID, useUiStore } from '../../store/ui';
import stakeSafeImg from '../../assets/incpt_safe_miniapp.webp';

/** Left-to-right vault order, so deposits group the way the picker reads. */
const VAULT_ORDER: VaultKind[] = ['liquid', 'monthly', 'maximum'];

type Sheet =
  | { kind: 'withdraw'; position: StakePosition }
  | { kind: 'lock'; position: StakePosition }
  | { kind: 'share'; position: StakePosition }
  | null;

export function StakeScreen() {
  const { t } = useTranslation();
  const {
    visible,
    vaults,
    legal,
    treasuryTrx,
    positions,
    summary,
    vaultOf,
    planOf,
    loading,
    fees,
    accrualEveryHours,
    tap,
  } = useStaking();
  const { tap: tapAction } = useStakingActions();
  // Own eye — not the home total. Also drops staking from the home sum.
  const balanceHidden = useUiStore((s) => !!s.coinBalanceHidden[STAKING_BALANCE_ID]);
  const toggleBalanceHidden = useUiStore((s) => s.toggleCoinBalanceHidden);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [celebrateWithdrawal, setCelebrateWithdrawal] = useState(false);
  const [focusVault, setFocusVault] = useState<VaultKind | null>(null);

  const showToast = (msg: string, duration = 2200) => {
    setToast(msg);
    window.setTimeout(() => setToast((m) => (m === msg ? null : m)), duration);
  };

  const showWithdrawalSuccess = (msg: string) => {
    showToast(msg, 4000);
    setCelebrateWithdrawal(true);
    window.setTimeout(() => setCelebrateWithdrawal(false), 2600);
  };

  // Current-line deposits first, grouped by vault; legacy ones last. Within a
  // group the largest deposit leads.
  const ordered = useMemo(
    () =>
      [...positions].sort((a, b) => {
        const rank = (p: StakePosition) =>
          p.vaultKind ? VAULT_ORDER.indexOf(p.vaultKind) : VAULT_ORDER.length;
        return rank(a) - rank(b) || b.current - a.current;
      }),
    [positions],
  );

  const metaOf = (p: StakePosition): PositionMeta => positionMeta(p, vaultOf(p), planOf(p), t);
  const active = sheet?.position ?? null;
  const activeMeta = active ? metaOf(active) : null;
  // Estimate today's earn: sum over positions of balance × daily rate.
  const todayEarn = useMemo(
    () => positions.reduce((sum, p) => sum + p.current * metaOf(p).dailyRate, 0),
    // metaOf closes over vaults/plans/t; positions is the varying input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [positions, vaults],
  );

  return (
    <div className="relative min-h-full">
      <ScreenBackdrop full />

      <div className="nav-pad-lg relative z-10 flex min-h-full flex-col pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <h1 className="text-center text-[16px] font-semibold tracking-[-0.2px] text-ink">
          {t('stake.title')}
        </h1>

        {/* Hero — safe illustration + total staked + today estimate */}
        <div className="flex flex-col items-center px-5 text-center">
          <div className="relative flex h-[150px] items-center justify-center">
            <StakeTapSafe
              image={stakeSafeImg}
              tap={tap}
              untilLabel={t('stake.tapUntil')}
              onReward={(amount) => showToast(t('stake.tapReward', { amount: fmtUsd(amount) }))}
              onError={(error) => {
                const raw = error instanceof Error ? error.message : String(error);
                showToast(
                  raw.includes('limit_exceeded')
                    ? t('stake.tapLimit')
                    : stakingErrorMessage(raw, t, t('stake.errGeneric')),
                );
              }}
              onSync={(body) => tapAction.mutateAsync(body)}
            />
          </div>
          {/* Amount centered on screen; eye hangs off the right edge (same as home). */}
          <div className="mt-4 flex w-full justify-center">
            <div className="relative">
              {balanceHidden ? (
                <span className="inline-block text-[34px] font-bold leading-none tracking-[-1.4px] tabular-nums text-ink">
                  {HIDDEN_BALANCE}
                </span>
              ) : (
                <CountUp
                  value={summary.total}
                  decimals={2}
                  className="inline-block text-[34px] font-bold leading-none tracking-[-1.4px] tabular-nums text-ink"
                />
              )}
              <button
                type="button"
                onClick={() => toggleBalanceHidden(STAKING_BALANCE_ID)}
                aria-label={balanceHidden ? t('wallet.showBalance') : t('wallet.hideBalance')}
                className="absolute left-full top-1/2 ml-1 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted active:bg-black/[0.05]"
              >
                {balanceHidden ? <EyeOffIcon size={22} /> : <EyeIcon size={22} />}
              </button>
            </div>
          </div>
          <span className="mt-1.5 text-[13px] text-muted">{t('stake.hero')}</span>

          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[rgba(52,199,89,0.12)] px-3.5 py-1.5">
            <BoltIcon />
            <span className="text-[12.5px] font-bold text-[#1f9d4d]">
              {t('stake.forecastTodayBadge', {
                amount: balanceHidden ? '••••' : fmtUsd(todayEarn),
              })}
            </span>
          </div>
        </div>

        {/* Open a deposit — hidden when closed and the user is not allowlisted. */}
        {visible && (
          <div className="mt-3.5 px-5">
            <StakeVaultPanel
              vaults={vaults}
              treasury={treasuryTrx}
              focusVault={focusVault}
              onFocusConsumed={() => setFocusVault(null)}
              onDone={showToast}
            />
          </div>
        )}

        {/* Existing deposits */}
        <div className={`px-5 ${visible ? 'mt-7' : 'mt-5'}`}>
          <h2 className="text-[15px] font-medium text-ink">{t('stake.positions')}</h2>

          {loading ? (
            <div className="mt-3 flex flex-col gap-2.5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-[20px] bg-white p-3.5 shadow-card">
                  <div className="skeleton h-3.5 w-28 rounded-full" />
                  <div className="skeleton mt-2.5 h-3 w-40 rounded-full" />
                  <div className="skeleton mt-3 h-[5px] w-full rounded-full" />
                </div>
              ))}
            </div>
          ) : ordered.length === 0 ? (
            <div className="mt-3 rounded-[20px] bg-white p-5 text-center shadow-card">
              <div className="text-[15px] font-medium text-ink">{t('stake.empty')}</div>
              {visible && (
                <p className="mt-1 text-[13px] leading-[1.5] text-muted">{t('stake.emptyHint')}</p>
              )}
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2.5">
              {ordered.map((p) => (
                <StakePositionCard
                  key={p.id}
                  position={p}
                  meta={metaOf(p)}
                  accrualEveryHours={accrualEveryHours}
                  onWithdraw={(x) => setSheet({ kind: 'withdraw', position: x })}
                  onLocked={(x) => setSheet({ kind: 'lock', position: x })}
                  onShare={(x) => setSheet({ kind: 'share', position: x })}
                  onTopUp={(x) => x.vaultKind && setFocusVault(x.vaultKind)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Terms — above the floating BottomNav hit target */}
        <div className="relative z-20 mt-7 px-5 pb-2 text-[11.5px] leading-[1.55] text-muted">
          <p>{t('stake.legal1')}</p>
          <p className="mt-2">{t('stake.legal2')}</p>
          <p className="mt-2">{t('stake.legal3')}</p>
          <div className="mt-2.5 flex flex-col gap-1.5">
            <LegalLink href={legal.terms}>{t('stake.terms')}</LegalLink>
            <LegalLink href={legal.aml}>{t('stake.aml')}</LegalLink>
            <LegalLink href={legal.privacy}>{t('stake.privacy')}</LegalLink>
          </div>
        </div>
      </div>

      <StakeWithdrawSheet
        position={sheet?.kind === 'withdraw' ? active : null}
        meta={sheet?.kind === 'withdraw' ? activeMeta : null}
        fees={fees}
        onClose={() => setSheet(null)}
        onDone={showWithdrawalSuccess}
      />
      <StakeLockSheet
        position={sheet?.kind === 'lock' ? active : null}
        meta={sheet?.kind === 'lock' ? activeMeta : null}
        onClose={() => setSheet(null)}
      />
      <StakeShareCard
        position={sheet?.kind === 'share' ? active : null}
        meta={sheet?.kind === 'share' ? activeMeta : null}
        onClose={() => setSheet(null)}
      />

      {toast && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-[calc(var(--tg-safe-top)_+_1rem)]">
          <div className="animate-toast max-w-full rounded-[18px] bg-ink px-4 py-2.5 text-center text-[14px] font-medium text-white shadow-[0px_10px_30px_rgba(0,0,0,0.25)]">
            {toast}
          </div>
        </div>
      )}
      {celebrateWithdrawal && <Confetti />}
    </div>
  );
}

function BoltIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="#22b45e" aria-hidden="true">
      <path d="M13 2 4.5 13.5H11L9.5 22 19 10h-6.5L13 2Z" />
    </svg>
  );
}

function LegalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words font-medium text-[#3fa8c6] no-underline"
    >
      {children}
    </a>
  );
}
